import {Injectable} from '@angular/core';
import {Subject} from 'rxjs';
import {Observable} from 'rxjs/Observable';

/**
 * Service to manage and observe fullscreen mode changes.
 * Provides methods to request or exit fullscreen mode and
 * emits updates on fullscreen status changes.
 *
 * @see {@link https://developer.mozilla.org/en-US/docs/Web/API/Fullscreen_API} for Fullscreen API details.
 * @see {@link https://angular.io/guide/dependency-injection} for more on Angular Dependency Injection.
 */
@Injectable()
export class FullscreenProvider {

  /**
   * Subject to emit fullscreen status changes.
   * @private
   */
  private fullscreenStatus: Subject<boolean> = new Subject<boolean>();

  /**
   * Observable to listen for fullscreen status changes.
   * Consumers can subscribe to this observable to get real-time updates.
   */
  public fullscreenStatus$: Observable<any> = this.fullscreenStatus.asObservable();

  constructor() {
    this.monitorFullscreenChanges();
  }

  /**
   * Requests the application to enter fullscreen mode.
   * This method uses the `requestFullscreen` API on the document's root element.
   *
   * @returns {Promise<void>} A promise that resolves when fullscreen mode is successfully entered.
   */
  public async requestFullscreen(): Promise<void> {
    try {
      await document.documentElement.requestFullscreen();
      // console.log('Entered fullscreen mode');
    } catch (err) {
      console.error(`Failed to enter fullscreen mode: ${err.message}`);
    }
  }

  /**
   * Requests the application to exit fullscreen mode.
   * This method uses the `exitFullscreen` API if fullscreen is currently active.
   *
   * @returns {Promise<void>} A promise that resolves when fullscreen mode is successfully exited.
   */
  public async exitFullscreen(): Promise<void> {
    try {
      if (document.fullscreenElement) {
        await document.exitFullscreen();
        // console.log('Exited fullscreen mode');
      }
    } catch (err) {
      console.error(`Failed to exit fullscreen mode: ${err.message}`);
    }
  }

  /**
   * Checks if the application is currently in fullscreen mode.
   *
   * @returns {boolean} `true` if the application is in fullscreen mode, `false` otherwise.
   */
  public isFullscreen(): boolean {
    return !!document.fullscreenElement;
  }

  /**
   * Sets up event listeners to monitor fullscreen mode changes and errors.
   * Emits updates to the `fullscreenStatus` Subject whenever fullscreen state changes.
   *
   * @private
   */
  private monitorFullscreenChanges(): void {
    // Listen for changes in fullscreen mode
    document.addEventListener('fullscreenchange', (): void => {
      const isFullscreen = !!document.fullscreenElement;
      this.fullscreenStatus.next(isFullscreen);
      // console.log(isFullscreen ? 'Entered fullscreen mode' : 'Exited fullscreen mode');
    });

    // Listen for fullscreen errors
    document.addEventListener('fullscreenerror', (event: Event): void => {
      console.error('Fullscreen error occurred:', event);
    });
  }

}



// WEBPACK FOOTER //
// ./src/providers/fullscreen/fullscreen.ts